import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
    filter, take, switchMap, map, withLatestFrom, concatMap, catchError, delay, tap, first,
} from 'rxjs/operators';

import { LoginSuccessAction } from '../../../pages/authentication/store/authentication.actions';
import { GA4EventType } from '../../analytics/GA4/models/general.model';
import { Ga4Event } from '../../analytics/GA4/store/ga4-analytics.actions';
import { WishlistCommandHandlerAdapter, makeCommandWishlistCommandHandler } from '../infrastructure/features/wishlist-command-handler.adapter';
import { QuantitySizeOptions } from '../item-product-cart/quantity-size-selector/quantity-size-selector.component';
import { ShopCartService } from '../services/shop-cart.service';
import { ShopCartMaxProductsErrorModalComponent } from '../shop-cart-max-products-error-modal/shop-cart-max-products-error-modal.component';

import {
    LoadShopCartSuccess,
    ShowShopCartError,
    DeleteItemsShopcart,
    AdjustItemQuantityShopcart,
    AdjustItemSizeShopcart,
    UpdateShopCart,
    LoadShopCartSuccessAndDeleteItemFromWishlist,
    AddGift,
    DeleteGift,
    LoadShopCartError,
    LoadShopCartErrorSuccess,
    SetShopCartSuccess,
    SetShopcartItemsStock,
    SetShopcartItemsSizes,
    SetShopcartItemsInCart,
    LoadShopcartWhenNewProducts,
    InitDataGiftPackage,
    LoadGiftPackageDataSuccess,
    ToggleSelectedBox,
    AddItemsPendingGetCart,
    InitShopCartShared,
} from './shopcart.actions';
import {
    canAddGift,
    getCartItems,
    getGiftBoxes,
    getPackingGiftPrice,
    getShopCartErrors,
    getShowAllItems,
    getTotalProducts,
} from './shopcart.selectors';

import { ApiCartService } from '@api/_api/api-cart/api-cart.service';
import { CartErrorService } from '@api/cart/cart-error.service';
import { Cart, CartItemUpdate, CartItemAdd, CartConstants, CartItemStock, PendingCartItem } from '@api/cart/cart.models';
import { CartService } from '@api/cart/cart.service';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue, getPage, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { StaticResourceService } from '@core/services/static-resource.service';
import { sidebarStatus } from '@features/sidebar/infrastructure/state/sidebar.selectors';
import { WishlistPublicEvents } from '@features/wishlist/public.api';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import { ErrorInfo } from '@shared/errors/models/error.models';
import { ShowError } from '@shared/errors/store/errors.actions';
import { Box } from '@shared/shopcart/store/shopcart.state';
import { ShopCartCoreApiService } from '@shared/shopcart-core/services/shopcart-core-api.service';
import {
    AddProductsShopcartCore,
    AddProductsShopcartCoreSuccess,
    GetShopCartCore,
    LoadShopCartCoreSuccess,
} from '@shared/shopcart-core/store/shopcart-core.actions';
import { getShopCartCore } from '@shared/shopcart-core/store/shopcart-core.selectors';
import { DialogMargins, DialogOptions, DialogService, DialogSizes } from '@ui/dialog/dialog.service';

@Injectable()
export class ShopCartEffects {
    private readonly storeAndLanguage$: Observable<StoreAndLanguage> = this.store
        .select(getStoreAndLanguage).pipe(filter((storeLang) => storeLang !== null), take(1));

    private readonly shopcart$: Observable<Cart> = this.store.select(getShopCartCore).pipe(filter((cart) => cart !== null));

    constructor(
        private readonly actions$: Actions,
        private readonly cartService: CartService,
        private readonly store: Store,
        private readonly shopCartService: ShopCartService,
        private readonly apiCartService: ApiCartService,
        private readonly cartErrorService: CartErrorService,
        private readonly modalService: DialogService,
        private readonly staticResourceService: StaticResourceService,
        private readonly translate: TranslateService,
        private readonly shopCartCoreApiService: ShopCartCoreApiService,
        @Inject(WINDOW) private readonly window: CommonWindow
    ) { }

    /**
     * Efecto para inicializar la cesta al inicio del modulo
     */
    public getShopCartCore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitShopCartShared),
            map(() => GetShopCartCore())
        );
    });

    /**
     * Efecto que refresca la cesta solo si hay nuevos productos
     */
    public setShopCartSharedWhenShopCartCoreEmit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitShopCartShared),
            switchMap(() => this.shopcart$),
            map((shopcart) => LoadShopCartSuccess({ shopcart }))
        );
    });

    /**
     * Inicializar la feature de la wishlist al InitShopCartShared
     */
    public getWishlistModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitShopCartShared),
            map(() => {
                const command = makeCommandWishlistCommandHandler();
                const newsletterConfirmationCommandHandler = new WishlistCommandHandlerAdapter(this.store);
                newsletterConfirmationCommandHandler.handle(command);
            })
        );
    }, { dispatch: false });

    /**
     * Efecto que refresca la cesta solo si hay nuevos productos
     */
    public getShopcartWhenNewProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopcartWhenNewProducts),
            concatLatestFrom(() => [
                this.store.select(getTotalProducts),
                this.store.select(getCartItems),
            ]),
            filter(([_, itemsInCart, cartItems]) => itemsInCart !== CartService.getItemsQuantity(cartItems)),
            map(() => GetShopCartCore())
        );
    });

    public loadShopCartSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartSuccess),
            concatLatestFrom(() => this.store.select(getShowAllItems)),
            map(([{ shopcart }, showAllItems]) => {
                // Mostramos el botón de más productos si disponemos de más productos en cesta que el valor por defecto en configuración
                const cart = shopcart;
                const moreMessage = 'dev.shopCart.moreProducts';

                let showMoreMessage = false;
                let moreItems = 0;
                let itemsInCart: number = 0;

                if (!cart.isEmpty) {
                    itemsInCart = CartService.getItemsQuantity(cart.items);

                    if (!showAllItems) {
                        moreItems = cart.items.length - CartConstants.ITEMS_TO_SHOW_DEFAULT;
                        showMoreMessage = cart.items.length > CartConstants.ITEMS_TO_SHOW_DEFAULT;
                    }
                }

                return SetShopCartSuccess({
                    showMoreMessage, moreMessage, moreItems, itemsInCart,
                });
            })
        );
    });

    /**
     * Efecto para establecer el stock de cada producto de la cesta
     */
    public addShopcartItemsStock$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartSuccess),
            filter(({ shopcart }) => shopcart.items.length > 0),
            switchMap(({ shopcart }) => this.shopCartService.cartItemsStockMap(shopcart.items)),
            map((stockItems: Record<string, CartItemStock>) => SetShopcartItemsStock({ stockItems }))
        );
    });

    /**
     * Efecto para establecer las tallas de cada producto de la cesta
     */
    public addShopcartItemsSizes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartSuccess),
            filter(({ shopcart }) => shopcart.items.length > 0),
            map(({ shopcart }) => this.shopCartService.cartItemsSizesMap(shopcart.items)),
            map((sizesItems: Record<string, QuantitySizeOptions[]>) => SetShopcartItemsSizes({ sizesItems }))
        );
    });

    public initialDeleteGiftWhenSomeItemIsNotStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartSuccess),
            concatLatestFrom(() => [this.store.select(canAddGift)]),
            filter(([{ shopcart }, canAdd]) => (shopcart.giftTicket || shopcart.giftPacking) && !canAdd),
            map(() => DeleteGift())
        );
    });

    public showShopCartError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ShowShopCartError),
            concatLatestFrom(() => this.store.select(getShopCartErrors)),
            map(([_, errors]: [any, string[]]) => {
                const errorInfo: ErrorInfo = {
                    subtitle: this.cartErrorService.getMessageOfError(errors),
                };
                return ShowError({ error: errorInfo, kind: 'shopcart' });
            })
        );
    });

    public deleteItemsShopcart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemsShopcart),
            withLatestFrom(this.storeAndLanguage$, this.shopcart$),
            filter(([action]) => action.items.length !== -1),
            concatMap(([action, { storeId, languageId }, cart]: [any, StoreAndLanguage, Cart]) => {
                const cartItems: CartItemUpdate[] = action.items.map((item) => {
                    const cartItem = cart.items
                        .find(i => i.id === item.id);
                    if (cartItem) {
                        return { id: cartItem.id, quantity: 0 };
                    }
                    return null;
                }).filter(item => item !== null);

                return this.apiCartService.setCart(storeId, languageId, cartItems).pipe(
                    switchMap(() => this.cartService.cart(storeId, languageId).pipe(
                        withLatestFrom([action])
                    )),
                    catchError((error) => this.handleError(error, cart))
                );
            }),
            map(([cart, action]: [Cart, any]) => LoadShopCartCoreSuccess(
                { shopcart: cart, goToCheckout: action.goToCheckout }
            ))
        );
    });

    public addItemsWishlistSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WishlistPublicEvents.AddItemsWishlistSuccessAction),
            filter(({ deleteItemCart }) => deleteItemCart),
            map(({ lastItems, goToCheckout }) => {
                return DeleteItemsShopcart({ items: lastItems, goToCheckout });
            }),
        );
    });

    public maxLengthItemsCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCore),
            concatLatestFrom(() => this.store.select(getCartItems).pipe(filter(value => !!value))),
            filter(([_, items]) => items.length >= 25),
            delay(500),
            tap(() => {
                this.modalService.openModalFromComponent(
                    '',
                    ShopCartMaxProductsErrorModalComponent,
                    {},
                    DialogSizes.SIZE_M,
                    DialogMargins.MARGIN_40,
                    [DialogOptions.FULL_MOBILE, DialogOptions.HIDE_TITLE]
                );
            })
        );
    }, {
        dispatch: false,
    });

    public addFeedProductsEmpathy$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCore),
            concatLatestFrom(() => [
                this.shopcart$,
                this.store.select(getCmsConfigValue('platform_search')),
            ]),
            delay(200),
            tap(([_, cart, isEmpathyPlatform]) => {
                // TODO ECOMDUTI-21029 el buscador ya esta en Angular y redux
                try {
                    if (!isEmpathyPlatform && this.window?.EmpathyX?.empathyXInitialized) {
                        cart.items.forEach(item => {
                            EmpathyX.productPage(item.id);
                            EmpathyX.addToCartOnProductPageClicked();
                        });
                    }
                } catch (e) {
                    sendObservabilityLog(e, 'addFeedProductsEmpathy$');
                }
            })
        );
    }, {
        dispatch: false,
    });

    public adjustItemQuantityShopcart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemQuantityShopcart),
            withLatestFrom(this.storeAndLanguage$, this.shopcart$),
            filter(([{ item }]) => !!item.sku),
            concatMap(([action, { storeId, languageId }, cart]: [any, StoreAndLanguage, Cart]) => {
                const newItem = cart.items.find((i) => i.sku === action.item.sku);
                const cartItems: CartItemUpdate[] = [{
                    id: newItem.id,
                    quantity: action.quantity,
                }];

                return this.apiCartService.setCart(storeId, languageId, cartItems).pipe(
                    switchMap(() => this.cartService.cart(storeId, languageId)),
                    catchError((error) => this.handleError(error, cart))
                );
            }),
            map(({ errors, ...cart }) => LoadShopCartCoreSuccess({ shopcart: { ...cart, errors } }))
        );
    });

    /**
     * Efecto para modificar la talla de los productos de la cesta
     */
    public adjustItemSizeShopcart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            withLatestFrom(this.storeAndLanguage$, this.shopcart$),
            filter(([{ item }]) => !!item.sku),
            concatMap((
                [{ item, size }, { storeId, languageId }, cart]: [{ item: any; size: any }, StoreAndLanguage, Cart]
            ) => {
                const analyticsInfo = {
                    origins: {
                        ...item?.analyticsInfo?.origins?.[0] ?? {},
                    },
                };

                const cartItemsAdd: CartItemAdd[] = [
                    // Producto a añadir
                    {
                        sku: size.sku.toString(),
                        parentId: item.parentId.toString(),
                        color: item.color,
                        quantity: item.quantity,
                        analyticsInfo: analyticsInfo,
                    },
                    // Producto a eliminar
                    {
                        sku: item.sku,
                        parentId: item.parentId.toString(),
                        color: item.color,
                        quantity: -Math.abs(item.quantity) - 1,
                        analyticsInfo: analyticsInfo,
                    },
                ];

                return this.shopCartCoreApiService.addCart(storeId, languageId, cartItemsAdd).pipe(
                    switchMap(() => this.cartService.cart(storeId, languageId)),
                    catchError((error) => this.handleError(error, cart))
                );
            }),
            map((cart: Cart) => LoadShopCartCoreSuccess({ shopcart: cart }))
        );
    });

    /**
     * Efecto para refrescar la cesta cuando añadimos un producto y la sidebar está abierta o estamos en la página de cesta
     */
    public refreshShopcartOnAddProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom(() => [
                this.store.select(sidebarStatus),
                this.store.select(getPage),
            ]),

            filter(([_, isSidebarOpen, page]) => isSidebarOpen || page === 'ItxAngularShopcartPage'),
            map(() => GetShopCartCore())
        );
    });

    public addProductToPendingGetCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            map(({ products }) => {
                const itemsPending: PendingCartItem[] = products.map(product => {
                    return {
                        sku: product.status.selectedSize.sku,
                        parentId: +product.mocaId,
                        colorId: product.status.selectedColor.id,
                    } as PendingCartItem;
                });
                return AddItemsPendingGetCart({ itemsPending });
            })
        );
    });

    /**
     * Efecto para incrementar el número de items del menú principal cuando añadimos un producto y la sidebar está cerrada
     */
    public increaseShopcartItemsOnMenu$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom(() => [
                this.store.select(sidebarStatus),
                this.store.select(getTotalProducts),
            ]),
            filter(([_, isSidebarOpen]) => !isSidebarOpen),
            map(([_, __, totalProducts]: [any, boolean, number]) => {
                return SetShopcartItemsInCart({ itemsInCart: totalProducts + 1 });
            })
        );
    });

    /**
     * Efecto para incluir los productos añadidos a la cesta a Empathy
     */
    public setEmpathyOnProductAddedToShopcart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('platform_search'))),
            tap(([{ cart }, isEmpathyPlatform]) => {
                try {
                    if (!isEmpathyPlatform && this.window?.EmpathyX?.empathyXInitialized) {
                        cart.items.forEach(item => {
                            EmpathyX.productPage(item.id);
                            EmpathyX.addToCartOnProductPageClicked();
                        });
                    }
                } catch (error) {
                    sendObservabilityLog(error, 'setempathyonproductAddedToShopcart$');
                    console.error('Error on Empathy');
                }
            })
        );
    }, { dispatch: false });

    public addItemCartWishSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCore),
            filter((action) => action.deleteItemFromWishlist),
            map(({ products }) => LoadShopCartSuccessAndDeleteItemFromWishlist({
                item: {
                    catentryId: products[0].colors[0].sizes[0].sku.toString(),
                    productId: products[0].id.toString(),
                },
            })),
        );
    });

    /**
     * Efecto para el tratamiento de errores de la cesta
     */
    public LoadShopCartError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartError),
            delay(500),
            map((action) => {
                // Para los errores de personalización, mostramos una modal
                if (action.errors.length > 0 && action.errors.includes('_ERR_CUSTOMIZATION_NOT_ALLOWED')) {
                    const errorInfo: ErrorInfo = {
                        subtitle: this.cartErrorService.getMessageOfError(action.errors),
                    };

                    return ShowError({ error: errorInfo, kind: 'general' });
                }

                return LoadShopCartErrorSuccess();
            })
        );
    });

    // TODO ECOMDUTI-21029 Se requiere para actualizar en el caso de que se añada un producto desde legacy, como por ejemplo una tarjeta de regalo y al loguearse
    public updateShopCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UpdateShopCart, LoginSuccessAction),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => this.storeAndLanguage$),
            concatMap((storeLang) => this.cartService.cart(storeLang.storeId, storeLang.languageId)),
            map((cart) => LoadShopCartCoreSuccess({ shopcart: cart }))
        );
    });

    private handleError(err: HttpErrorResponse, cart: Cart): Observable<Cart> {
        return of({ ...cart, errors: [err?.error?.key] });
    }

    public deleteGift$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteGift),
            concatLatestFrom(() => [this.storeAndLanguage$, this.shopcart$]),
            concatMap(([_, { storeId, languageId }, cart]) => {
                return this.apiCartService.deleteGift(storeId, languageId, cart.id).pipe(
                    concatMap(() => this.apiCartService.deleteGiftTicket(storeId, languageId, cart.id)),
                    switchMap(() => this.cartService.cart(storeId, languageId)),
                    catchError((error: HttpErrorResponse) => this.handleError(error, cart))
                );
            }),
            map((cart: Cart) => LoadShopCartCoreSuccess({ shopcart: cart }))
        );
    });

    public addGift$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddGift),
            concatLatestFrom(() => [this.storeAndLanguage$, this.shopcart$, this.store.select(getGiftBoxes)]),
            concatMap(([action, { storeId, languageId }, cart, boxes]) => {
                let checkedBox: Box = boxes.find((x) => x.checked);
                let giftMessage: string = action.giftMessage || '';
                let onlyMessage: boolean = checkedBox.type !== 'gift';
                return this.apiCartService.addGift(storeId, languageId, cart.id, giftMessage, onlyMessage.toString()).pipe(
                    switchMap(() => this.apiCartService
                        .addGiftTicket(storeId, languageId, cart.id, action.giftTicket)),
                    switchMap(() => this.cartService.cart(storeId, languageId)),
                    catchError((error: HttpErrorResponse) => this.handleError(error, cart)),
                    map((shopcart) => LoadShopCartCoreSuccess({ shopcart }))
                );
            })
        );
    });

    public initDataGiftPackage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitDataGiftPackage),
            concatLatestFrom(() => [this.store.select(getPackingGiftPrice), this.store.select(getGiftBoxes)]),
            filter(([_action, _giftPrice, giftBoxes]) => giftBoxes === null),
            map(([_action, giftPrice]) => {
                const boxes = [
                    {
                        name: this.translate.instant('dev.cart.boxEco'),
                        type: 'standard',
                        image: this.staticResourceService.getImage('/giftTicket/standard-box.jpg', true),
                        price: 0,
                        checked: true,
                    } as Box,
                    {
                        name: this.translate.instant('dev.cart.giftBox'),
                        type: 'gift',
                        image: this.staticResourceService.getImage('/giftTicket/gift-box.jpg', true),
                        price: giftPrice,
                        checked: false,
                    } as Box,
                ];
                return LoadGiftPackageDataSuccess({ boxes });
            })
        );
    });

    public toggleSelectedBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ToggleSelectedBox),
            concatLatestFrom(() => this.store.select(getGiftBoxes)),
            map(([action, boxes]) => {
                const newBoxes: Box[] = boxes.map((box) => {
                    return { ...box, checked: action.typeBox === box.type };
                });
                return LoadGiftPackageDataSuccess({ boxes: newBoxes });
            })
        );
    });

    public updateShopcartOnNavigateToCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(({ page }) => page === 'ItxAngularShopcartPage'),
            map(() => GetShopCartCore())
        );
    });

    public emptyShopcartItemsPageviewGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemsShopcart),
            concatLatestFrom(() => this.store.select(getPage)),
            filter(([_action, page]) => page === 'ItxAngularShopcartPage'),
            concatMap(() => this.actions$.pipe(ofType(SetShopCartSuccess)).pipe(first())),
            filter((action) => action.itemsInCart === 0),
            map(() => Ga4Event({ name: GA4EventType.page_view }))
        );
    });

    public goToCheckoutOnUpadeCartSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadShopCartCoreSuccess),
            filter(({ goToCheckout }) => goToCheckout),
            tap(() => {
                this.shopCartService.processCart();
            })
        );
    }, { dispatch: false });
}
